import { z } from 'zod';

const configSchema = z.object({
  REACT_APP_IS_SERVER_DEV: z.boolean().default(false),
  REACT_APP_API_URL: z.string().url(),

  REACT_APP_SITE_TITLE: z.string().min(1),
  REACT_APP_SITE_DESCRIPTION: z.string().min(1),

  REACT_APP_COMPANY_STATIC_ROOT_FOLDER: z.string().min(1),
  REACT_APP_COMPANY_STATIC_FOLDER: z.string().min(1),
  REACT_APP_SITE_FAVICON: z.string().min(1),
  REACT_APP_SITE_ICON_192: z.string().min(1),
  REACT_APP_SITE_ICON_512: z.string().min(1),
  REACT_APP_SITE_MANIFEST: z.string().min(1),

  REACT_APP_RELOAD_CAR_CAR__FULL_NAME: z.string().default("Reload Car-Car"),
  REACT_APP_RELOAD_CAR_CAR__SHORT_NAME: z.string().default("C-C"),
  REACT_APP_RELOAD_CAR_TERMINAL_CAR__FULL_NAME: z.string().default("Reload Car-Terminal-Car"),
  REACT_APP_RELOAD_CAR_TERMINAL_CAR__SHORT_NAME: z.string().default("C-T-C"),
  REACT_APP_INTO_PLUKK_STORAGE__FULL_NAME: z.string().default("Into Plukk Storage"),
  REACT_APP_INTO_PLUKK_STORAGE__SHORT_NAME: z.string().default("Plukk"),
  REACT_APP_CROSS_DOCK__FULL_NAME: z.string().default("Cross Dock"),
  REACT_APP_CROSS_DOCK__SHORT_NAME: z.string().default("CD"),
  REACT_APP_INTO_PLUKK_STORAGE__FULL_NAME_FOR_ORDER_IMPORT: z.string().default("OP"),
  REACT_APP_COLD_STORAGE__FULL_NAME: z.string().default("Cold Storage"),
  REACT_APP_COLD_STORAGE__SHORT_NAME: z.string().default("CS"),
  REACT_APP_PER__FULL_NAME: z.string().default("PER"),
  REACT_APP_PER__SHORT_NAME: z.string().default("PER"),

  REACT_APP_RELOAD_CAR_CAR__HIDDEN: z.boolean().default(false),
  REACT_APP_RELOAD_CAR_TERMINAL_CAR__HIDDEN: z.boolean().default(false),
  REACT_APP_INTO_PLUKK_STORAGE__HIDDEN: z.boolean().default(false),
  REACT_APP_CROSS_DOCK__HIDDEN: z.boolean().default(false),

  REACT_APP_4_HOURS_AHEAD_FILTER__HIDDEN: z.boolean().default(false),

  // Feature flags
  REACT_APP_FF_STORAGE_MODULE: z.boolean().default(false),
  REACT_APP_FF_DEVS_ATTENTION_ALERT: z.boolean().default(false),
  REACT_APP_FF_COMBINED_SCANNER_MANUAL_MODE: z.boolean().default(false),
  REACT_APP_IS_PROD: z.boolean().default(false),

  // BLE Scanner
  REACT_APP_BLE_SCANNER__SERVICE_UUID: z.string().optional(),
  REACT_APP_BLE_SCANNER__SERVICE_CHAR_UUID: z.string().optional(),
  REACT_APP_BLE_SCANNER__SCANNER_NAME: z.string().optional(),
  REACT_APP_BLE_SCANNER__SCANNER_PREFIX: z.string().optional(),
});

// Mirror the backend's parse_bool contract (osfc/core/env_settings.py) so both
// sides agree on what a boolean env value looks like. The env files mix "1"/"0"
// with "true"/"false"/"False", so accept the common truthy/falsy tokens.
const TRUTHY = new Set(['1', 'true', 't', 'yes', 'y', 'on']);
const FALSY = new Set(['0', 'false', 'f', 'no', 'n', 'off']);

const parseBoolean = (value) => {
  // Leave unset values to zod so the field's .default(...) applies.
  if (value === undefined || value === null || value === '') return undefined;
  const token = String(value).trim().toLowerCase();
  if (TRUTHY.has(token)) return true;
  if (FALSY.has(token)) return false;
  throw new Error(`cannot interpret ${JSON.stringify(value)} as bool`);
};

const createConfig = () => {
  try {
    return configSchema.parse({
      REACT_APP_IS_SERVER_DEV: parseBoolean(process.env.REACT_APP_IS_SERVER_DEV),
      REACT_APP_API_URL: process.env.REACT_APP_API_URL,

      REACT_APP_SITE_TITLE: process.env.REACT_APP_SITE_TITLE,
      REACT_APP_SITE_DESCRIPTION: process.env.REACT_APP_SITE_DESCRIPTION,

      REACT_APP_COMPANY_STATIC_ROOT_FOLDER: process.env.REACT_APP_COMPANY_STATIC_ROOT_FOLDER,
      REACT_APP_COMPANY_STATIC_FOLDER: process.env.REACT_APP_COMPANY_STATIC_FOLDER,
      REACT_APP_SITE_FAVICON: process.env.REACT_APP_SITE_FAVICON,
      REACT_APP_SITE_ICON_192: process.env.REACT_APP_SITE_ICON_192,
      REACT_APP_SITE_ICON_512: process.env.REACT_APP_SITE_ICON_512,
      REACT_APP_SITE_MANIFEST: process.env.REACT_APP_SITE_MANIFEST,

      REACT_APP_RELOAD_CAR_CAR__FULL_NAME: process.env.REACT_APP_RELOAD_CAR_CAR__FULL_NAME,
      REACT_APP_RELOAD_CAR_CAR__SHORT_NAME: process.env.REACT_APP_RELOAD_CAR_CAR__SHORT_NAME,
      REACT_APP_RELOAD_CAR_TERMINAL_CAR__FULL_NAME: process.env.REACT_APP_RELOAD_CAR_TERMINAL_CAR__FULL_NAME,
      REACT_APP_RELOAD_CAR_TERMINAL_CAR__SHORT_NAME: process.env.REACT_APP_RELOAD_CAR_TERMINAL_CAR__SHORT_NAME,
      REACT_APP_INTO_PLUKK_STORAGE__FULL_NAME: process.env.REACT_APP_INTO_PLUKK_STORAGE__FULL_NAME,
      REACT_APP_INTO_PLUKK_STORAGE__SHORT_NAME: process.env.REACT_APP_INTO_PLUKK_STORAGE__SHORT_NAME,
      REACT_APP_CROSS_DOCK__FULL_NAME: process.env.REACT_APP_CROSS_DOCK__FULL_NAME,
      REACT_APP_CROSS_DOCK__SHORT_NAME: process.env.REACT_APP_CROSS_DOCK__SHORT_NAME,

      REACT_APP_INTO_PLUKK_STORAGE__FULL_NAME_FOR_ORDER_IMPORT: process.env.REACT_APP_INTO_PLUKK_STORAGE__FULL_NAME_FOR_ORDER_IMPORT,
      REACT_APP_COLD_STORAGE__FULL_NAME: process.env.REACT_APP_COLD_STORAGE__FULL_NAME,
      REACT_APP_COLD_STORAGE__SHORT_NAME: process.env.REACT_APP_COLD_STORAGE__SHORT_NAME,
      REACT_APP_PER__FULL_NAME: process.env.REACT_APP_PER__FULL_NAME,
      REACT_APP_PER__SHORT_NAME: process.env.REACT_APP_PER__SHORT_NAME,

      REACT_APP_RELOAD_CAR_CAR__HIDDEN: parseBoolean(process.env.REACT_APP_RELOAD_CAR_CAR__HIDDEN),
      REACT_APP_RELOAD_CAR_TERMINAL_CAR__HIDDEN: parseBoolean(process.env.REACT_APP_RELOAD_CAR_TERMINAL_CAR__HIDDEN),
      REACT_APP_INTO_PLUKK_STORAGE__HIDDEN: parseBoolean(process.env.REACT_APP_INTO_PLUKK_STORAGE__HIDDEN),
      REACT_APP_CROSS_DOCK__HIDDEN: parseBoolean(process.env.REACT_APP_CROSS_DOCK__HIDDEN),

      REACT_APP_4_HOURS_AHEAD_FILTER__HIDDEN: parseBoolean(process.env.REACT_APP_4_HOURS_AHEAD_FILTER__HIDDEN),

      REACT_APP_FF_STORAGE_MODULE: parseBoolean(process.env.REACT_APP_FF_STORAGE_MODULE),
      REACT_APP_FF_DEVS_ATTENTION_ALERT: parseBoolean(process.env.REACT_APP_FF_DEVS_ATTENTION_ALERT),
      REACT_APP_FF_COMBINED_SCANNER_MANUAL_MODE: parseBoolean(process.env.REACT_APP_FF_COMBINED_SCANNER_MANUAL_MODE),
      REACT_APP_IS_PROD: parseBoolean(process.env.REACT_APP_IS_PROD),

      REACT_APP_BLE_SCANNER__SERVICE_UUID: process.env.REACT_APP_BLE_SCANNER__SERVICE_UUID,
      REACT_APP_BLE_SCANNER__SERVICE_CHAR_UUID: process.env.REACT_APP_BLE_SCANNER__SERVICE_CHAR_UUID,
      REACT_APP_BLE_SCANNER__SCANNER_NAME: process.env.REACT_APP_BLE_SCANNER__SCANNER_NAME,
      REACT_APP_BLE_SCANNER__SCANNER_PREFIX: process.env.REACT_APP_BLE_SCANNER__SCANNER_PREFIX,
    });
  } catch (error) {
    console.error('❌ Invalid environment configuration:');
    console.error(error);
    throw new Error('Application failed to start due to invalid configuration. Check your environment variables.');
  }
};

export const envSettings = createConfig();
